import { QueryClient } from '@tanstack/react-query';

// สร้าง QueryClient พร้อม configuration ที่ปิด cache ทั้งหมด
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // เก็บ cache เพียงเล็กน้อยเพื่อให้ refetch ทำงานได้
      staleTime: 0, // ข้อมูล stale ทันที = ดึงใหม่ทุกครั้ง
      gcTime: 1000, // เก็บ cache แค่ 1 วินาที (เพื่อให้ refetch ทำงาน)
      
      // ปิด background refetch ทั้งหมด
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true, // ดึงข้อมูลใหม่ทุกครั้งที่ mount component
      
      // Retry policy
      retry: 1, // ลดการ retry เหลือ 1 ครั้ง
      retryDelay: 1000, // รอ 1 วินาทีก่อน retry
      
      // Network mode - ใช้ online only
      networkMode: 'online',
    },
    mutations: {
      // retry ต้องเป็น 0 — mutation เป็น non-idempotent (POST/PUT/DELETE)
      // การ retry การสร้างข้อมูลเสี่ยงสร้าง row ซ้ำถ้า request แรกสำเร็จฝั่ง server
      // แต่ response หาย · อีกทั้ง retry error 4xx ไม่มีประโยชน์ (double toast/log)
      retry: 0,

      // ไม่เก็บ cache สำหรับ mutations
      gcTime: 0,

      // networkMode ต้องเป็น 'always' — ถ้าใช้ 'online' ตอน offline mutation จะเข้า
      // สถานะ paused เงียบๆ (ไม่ error ไม่มี toast ปุ่ม submit ค้าง disabled) แล้ว
      // auto-resume ยิง POST เมื่อกลับ online แม้ผู้ใช้กดปิด modal ไปแล้ว = เสี่ยง
      // สร้างรายการที่ผู้ใช้คิดว่ายกเลิกไป · 'always' ทำให้ offline fail ทันที
      // → เข้า onError → toast แจ้งผู้ใช้ (ทุก mutation มี onError handler อยู่แล้ว)
      networkMode: 'always',
    },
  },
});

// Cache keys สำหรับ queries ต่างๆ
export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
    permissions: ['auth', 'permissions'] as const,
  },
  
  // Companies
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.companies.details(), id] as const,
    flowAccount: ['companies', 'flowAccount'] as const,
  },
  
  // Rentals
  rentals: {
    all: ['rentals'] as const,
    lists: () => [...queryKeys.rentals.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.rentals.lists(), filters] as const,
    details: () => [...queryKeys.rentals.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.rentals.details(), id] as const,
    history: (id: number) => [...queryKeys.rentals.detail(id), 'history'] as const,
    statistics: ['rentals', 'statistics'] as const,
  },
  
  // Devices
  devices: {
    all: ['devices'] as const,
    lists: () => [...queryKeys.devices.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.devices.lists(), filters] as const,
    details: () => [...queryKeys.devices.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.devices.details(), id] as const,
    available: ['devices', 'available'] as const,
    maintenance: (id: number) => [...queryKeys.devices.detail(id), 'maintenance'] as const,
    notes: (id: number) => [...queryKeys.devices.detail(id), 'notes'] as const,
  },
  
  // Contracts
  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.contracts.details(), id] as const,
  },
  
  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    summary: ['dashboard', 'summary'] as const,
    revenue: ['dashboard', 'revenue'] as const,
    trends: ['dashboard', 'trends'] as const,
    charts: ['dashboard', 'charts'] as const,
  },
  
  // Reports
  reports: {
    all: ['reports'] as const,
    financial: (params?: any) => [...queryKeys.reports.all, 'financial', params] as const,
    rentals: (params?: any) => [...queryKeys.reports.all, 'rentals', params] as const,
    devices: (params?: any) => [...queryKeys.reports.all, 'devices', params] as const,
    expiring: (params?: any) => [...queryKeys.reports.all, 'expiring', params] as const,
  },
  
  // Master Data
  sites: {
    all: ['sites'] as const,
    list: (filters?: any) => [...queryKeys.sites.all, 'list', filters] as const,
    detail: (id: number) => [...queryKeys.sites.all, 'detail', id] as const,
  },
  customers: {
    all: ['customers'] as const,
    list: (filters?: any) => [...queryKeys.customers.all, 'list', filters] as const,
  },
  products: {
    all: ['products'] as const,
    list: (filters?: any) => [...queryKeys.products.all, 'list', filters] as const,
  },
  productTypes: {
    all: ['productTypes'] as const,
    list: () => [...queryKeys.productTypes.all, 'list'] as const,
  },
  brands: {
    all: ['brands'] as const,
    list: () => [...queryKeys.brands.all, 'list'] as const,
    active: () => [...queryKeys.brands.all, 'active'] as const,
    inactive: () => [...queryKeys.brands.all, 'inactive'] as const,
  },
  suppliers: {
    all: ['suppliers'] as const,
    list: () => [...queryKeys.suppliers.all, 'list'] as const,
    active: () => [...queryKeys.suppliers.all, 'active'] as const,
  },
  maintenanceTypes: {
    all: ['maintenanceTypes'] as const,
    list: () => [...queryKeys.maintenanceTypes.all, 'list'] as const,
    active: () => [...queryKeys.maintenanceTypes.all, 'active'] as const,
    inactive: () => [...queryKeys.maintenanceTypes.all, 'inactive'] as const,
  },
  repairMethods: {
    all: ['repairMethods'] as const,
    list: () => [...queryKeys.repairMethods.all, 'list'] as const,
    active: () => [...queryKeys.repairMethods.all, 'active'] as const,
  },
  sims: {
    all: ['sims'] as const,
    list: (filters?: any) => [...queryKeys.sims.all, 'list', filters] as const,
    available: ['sims', 'available'] as const,
  },
  
  // Settings
  settings: {
    general: ['settings', 'general'] as const,
    notifications: ['settings', 'notifications'] as const,
    flowAccount: ['settings', 'flowAccount'] as const,
  },
  
  // Users & Roles
  users: {
    all: ['users'] as const,
    list: (filters?: any) => [...queryKeys.users.all, 'list', filters] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: () => [...queryKeys.roles.all, 'list'] as const,
  },
  
  // Authorized Signers
  authorizedSigners: {
    all: ['authorizedSigners'] as const,
    lists: () => [...queryKeys.authorizedSigners.all, 'list'] as const,
    list: (filters?: any) => [...queryKeys.authorizedSigners.lists(), filters] as const,
    details: () => [...queryKeys.authorizedSigners.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.authorizedSigners.details(), id] as const,
    byCompany: (companyId: number) => [...queryKeys.authorizedSigners.all, 'company', companyId] as const,
  },
};

// Utility functions สำหรับ query management (ไม่มี cache)
export const cacheUtils = {
  // Invalidate specific queries - บังคับ refetch ข้อมูลใหม่
  invalidate: async (keys: readonly unknown[]) => {
    await queryClient.invalidateQueries({ queryKey: keys });
  },
  
  // Invalidate multiple queries - บังคับ refetch หลาย queries
  invalidateMultiple: async (keysList: readonly (readonly unknown[])[]) => {
    await Promise.all(
      keysList.map(keys => queryClient.invalidateQueries({ queryKey: keys }))
    );
  },
  
  // Reset specific queries - ลบข้อมูลและ error state
  reset: async (keys: readonly unknown[]) => {
    await queryClient.resetQueries({ queryKey: keys });
  },
  
  // Refetch queries - บังคับดึงข้อมูลใหม่ทันที
  refetch: async (keys: readonly unknown[]) => {
    await queryClient.refetchQueries({ queryKey: keys });
  },
  
  // Cancel queries - ยกเลิก request ที่กำลังทำงาน
  cancel: async (keys: readonly unknown[]) => {
    await queryClient.cancelQueries({ queryKey: keys });
  },
  
  // Remove queries - ลบ query ออกจาก query client
  remove: (keys: readonly unknown[]) => {
    queryClient.removeQueries({ queryKey: keys });
  },
};